import api from "./api";

export type InvoiceStatus = "draft" | "open" | "pending" | "paid" | "cancelled" | "void" | "uncollectible";

export type Invoice = {
  id: string;
  club_id: string;
  invoice_number: string | null;
  // Assigned at creation; the fiscal number arrives only on payment.
  proforma_number?: string | null;
  issued_at?: string | null;
  oblio_link?: string | null;
  oblio_error?: string | null;
  stripe_invoice_id: string;
  stripe_payment_link: string | null;
  amount_cents: number;
  currency: string;
  status: InvoiceStatus;
  period_start: string | null;
  period_end: string | null;
  athlete_count: number | null;
  paid_at: string | null;
  pdf_url: string | null;
  hosted_invoice_url: string | null;
  created_at: string;
  club?: {
    id: string;
    name: string;
  };
};

/**
 * Get all invoices (optionally filtered by club)
 */
export async function getInvoices(clubId?: string) {
  const qs = clubId ? `?clubId=${clubId}` : "";
  const { data } = await api.get<Invoice[]>(`/invoices${qs}`);
  return data;
}

/**
 * Get invoice details
 */
export async function getInvoiceById(invoiceId: string) {
  const { data } = await api.get<Invoice>(`/invoices/${invoiceId}`);
  return data;
}

/**
 * Generate monthly invoices (Platform Admin only)
 */
export async function generateMonthlyInvoices(month?: number, year?: number) {
  const { data } = await api.post(`/invoices/generate-monthly`, {
    month,
    year,
  });
  return data;
}

/**
 * Mark invoice as paid (Platform Admin only)
 */
export async function markInvoicePaid(invoiceId: string) {
  const { data } = await api.patch(`/invoices/${invoiceId}/mark-paid`);
  return data;
}

/**
 * Generate invoice for a specific club with Stripe payment link (Platform Admin only)
 * @param clubId - Club ID
 * @param periodStart - Optional billing period start date (ISO string)
 * @param periodEnd - Optional billing period end date (ISO string)
 */
export async function generateInvoiceForClub(
  clubId: string,
  periodStart?: string,
  periodEnd?: string
) {
  const { data } = await api.post<{
    message: string;
    invoice: Invoice;
    payment_link?: string;
    stripe_error?: string;
    prorata_details?: {
      total_days: number;
      full_period_athletes: number;
      prorata_athletes: number;
      daily_rate_cents: number;
    };
  }>(`/invoices/generate-for-club/${clubId}`, {
    periodStart,
    periodEnd,
  });
  return data;
}

/**
 * Update invoice status (Platform Admin only)
 */
export async function updateInvoiceStatus(invoiceId: string, status: string) {
  const { data } = await api.patch<Invoice>(`/invoices/${invoiceId}/status`, { status });
  return data;
}

/**
 * Delete an invoice (Platform Admin only)
 */
export async function deleteInvoice(invoiceId: string) {
  const { data } = await api.delete<{ success: boolean; message: string }>(`/invoices/${invoiceId}`);
  return data;
}
